import type { StateCreator } from 'zustand'
import { chatBuilderService } from '@/services/chatBuilder'
import { storeLogger } from '@/utils/logger'
import type { AutoSaveState } from './types'

export interface AutoSaveSlice extends AutoSaveState {
  // Auto-save actions
  enableAutoSave: () => void
  disableAutoSave: () => void
  triggerAutoSave: () => Promise<void>
}

// Jev settings save only through their own control, so a stale copy here cannot revert them.
function withoutJevSettings(
  productConfig: Record<string, unknown> | undefined
) {
  const { jev: _jev, ...rest } = productConfig ?? {}

  return rest
}

export const createAutoSaveSlice: StateCreator<
  AutoSaveSlice & {
    currentConfiguration: any
    hasUnsavedChanges: boolean
  },
  [],
  [],
  AutoSaveSlice
> = (set, get) => ({
  // Initial state
  autoSaveEnabled: false,
  lastAutoSave: null,

  // Auto-save actions
  enableAutoSave: () => {
    storeLogger.debug('Auto-save enabled', {})
    set({ autoSaveEnabled: true })
  },

  disableAutoSave: () => {
    storeLogger.debug('Auto-save disabled', {})
    set({ autoSaveEnabled: false })
  },

  triggerAutoSave: async () => {
    const { currentConfiguration, hasUnsavedChanges, autoSaveEnabled } = get()

    if (
      !currentConfiguration ||
      !currentConfiguration.id ||
      !hasUnsavedChanges ||
      !autoSaveEnabled
    ) {
      storeLogger.debug('Auto-save skipped', {
        hasConfig: !!currentConfiguration,
        hasId: !!currentConfiguration?.id,
        hasChanges: hasUnsavedChanges,
        enabled: autoSaveEnabled,
      })
      return
    }

    try {
      // Design is omitted on purpose: resending it here would rewrite stored keys
      // and break design undo.
      const cleanData = {
        rulesConfig: currentConfiguration.rulesConfig || {},
        productConfig: withoutJevSettings(currentConfiguration.productConfig),
      }

      storeLogger.debug('Auto-save triggered', {
        configId: currentConfiguration.id,
      })

      await chatBuilderService.updateConfiguration(
        currentConfiguration.id,
        cleanData
      )

      // Note: We intentionally do NOT refetch queries here.
      // The local Zustand state is the source of truth during editing.
      // Refetching would cause unnecessary re-renders and potential race conditions.

      set({
        lastAutoSave: new Date(),
      })

      // Check for unpublished changes after auto-save
      const state = get() as any
      if (state.checkUnpublishedChanges) {
        state.checkUnpublishedChanges()
      }

      // Note: hasUnsavedChanges should be cleared from the configuration slice
      storeLogger.debug('Auto-save completed', {
        configId: currentConfiguration.id,
      })
    } catch (error) {
      storeLogger.error('Auto-save failed', error as Error)

      // Disable auto-save if authentication fails
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = error.message as string
        if (
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('401')
        ) {
          storeLogger.warn('Auto-save disabled due to auth failure', {})
          set({ autoSaveEnabled: false })
        }
      }
      // Throw error so main store can handle retry logic
      throw error
    }
  },
})
